import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { Breadcrumbs, EmptyState, PageSkeleton } from "@/components/ui";
import { VettingPage } from "@/components/vetting/vetting-page";
import { useTestingCompanyVetting } from "@/hooks/use-access-requests";

export const Route = createFileRoute("/_authenticated/testers/access-requests/$companyId")({
  component: VettingRoute,
});

/**
 * The link target for a company row of the Testers › Access requests inbox:
 * the utility's full-page vetting of one company. The route owns the read's
 * loading, 404, 403 and error states; the page mounts only once the picture
 * has loaded. The read answers for ANY existing edge — a suspended or
 * long-revoked company's history reviews here as readily as a pending ask.
 */
function VettingRoute() {
  const { t } = useTranslation(["testers", "common"]);
  const { companyId } = Route.useParams();
  const query = useTestingCompanyVetting(companyId);
  const vetting = query.data;

  if (!vetting) {
    if (!query.isError) {
      return (
        <div className="p-8">
          <PageSkeleton />
        </div>
      );
    }
    const status = query.error instanceof ApiError ? query.error.status : null;
    return (
      <div className="p-8">
        {status === 403 ? (
          <EmptyState title={t("testers:vetting.states.forbidden")} />
        ) : status === 404 ? (
          <EmptyState title={t("testers:vetting.states.missing")} />
        ) : (
          <EmptyState
            title={t("testers:vetting.states.loadError")}
            actionLabel={t("testers:vetting.states.retry")}
            onAction={() => void query.refetch()}
          />
        )}
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("testers:page.tabs.testers"), to: "/testers" },
          {
            label: t("testers:vetting.breadcrumb.inbox"),
            to: "/testers",
            search: { tab: "requests" },
          },
          { label: vetting.name },
        ]}
      />
      <div className="mt-4">
        <VettingPage vetting={vetting} />
      </div>
    </div>
  );
}
